import { identify } from '@ramp106/omrjs-tracking-reviews'
import type { AccountType } from '@ramp106/omrjs-tracking-reviews'
import * as Sentry from '@sentry/vue'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { authority, requiredProfileScopes } from '@/config/env'
import { type Role, type MeQuery, ProfileScopeEnum, RoleNameEnum } from '@/gql/myomr'
import { AccountTypeEnum, type ReviewsCurrentUserQuery } from '@/gql/reviews'
import { toTypeAndId } from '@/helpers/globalIdHelper'
import { redirectToLogin } from '@/helpers/loginHelper'
import type { ID } from '@/types'

import { useLocaleStore } from './locale'

const isProfileComplete = function (profileScopes: string[]) {
  return requiredProfileScopes.every((scope) => profileScopes?.includes(scope))
}

type ReviewsAnalyticsIdentity = NonNullable<NonNullable<ReviewsCurrentUserQuery['manage']>['currentUser']>

const ACCOUNT_TYPE_BY_ENUM: Record<AccountTypeEnum, AccountType> = {
  [AccountTypeEnum.Agency]: 'agency',
  [AccountTypeEnum.Reviewer]: 'reviewer',
  [AccountTypeEnum.Vendor]: 'vendor',
}

export const persistKey = 'omr-manage-me'
export const useMeStore = defineStore(
  'me',
  () => {
    const localeStore = useLocaleStore()
    const currentPartnerCompanyGlobalId = ref<ID | undefined>(undefined)
    const roles = ref<Role[]>([])
    const boothsVisible = ref(false)
    const hasPremiumBooths = ref(false)

    const currentPartnerCompanyId = computed<string>(() =>
      currentPartnerCompanyGlobalId.value ? (toTypeAndId(currentPartnerCompanyGlobalId.value)['id'] as string) : '',
    )
    const hasCurrentPartnerCompany = computed(() => !!currentPartnerCompanyId.value)
    const currentRoles = computed(() => {
      if (!currentPartnerCompanyId.value) {
        const AllowedNoPartnerCompanyRoles = [RoleNameEnum.QuotaUser]

        return (roles.value ?? []).filter((role: Role) => {
          return AllowedNoPartnerCompanyRoles.includes(role.name)
        })
      }

      return (roles.value ?? []).filter((role: Role) => {
        return (
          role.name === RoleNameEnum.QuotaUser ||
          (role.resourceType === 'PartnerCompany' && role.resourceId == currentPartnerCompanyId.value)
        )
      })
    })

    const rawMe = ref<MeQuery['me'] | undefined>()
    const userFirstName = ref('')
    const userLastName = ref('')
    const userEmail = ref('')
    const userId = ref('')
    const userProfileImagePath = ref('')
    const userName = computed(() => [userFirstName.value, userLastName.value].filter((name) => name != '').join(' '))
    const profileScopes = ref<ProfileScopeEnum[]>([])
    const hasAcceptedReviewsTerms = ref(false)

    const userProfileImage = computed(() => {
      if (userProfileImagePath.value) {
        if (userProfileImagePath.value.startsWith('http')) {
          return userProfileImagePath.value
        } else {
          return `${authority}${userProfileImagePath.value}`
        }
      } else {
        return null
      }
    })

    const leadGenLimit = computed(() => {
      return (eventSlug: string) => {
        return rawMe.value?.partnerCompany?.leadGenLimitConfigs.find((l) => l.event.slug == eventSlug)?.limit
      }
    })

    function setCurrentPartnerCompanyGlobalId(id: ID) {
      currentPartnerCompanyGlobalId.value = id
    }

    function setupSentryUser(me: NonNullable<MeQuery['me']>) {
      Sentry.setUser({ id: me.id })
    }

    let hasIdentified = false
    let reviewsIdentityResolved = false
    let reviewsIdentity: ReviewsAnalyticsIdentity | undefined

    function identifyUser(analyticsIdentity?: ReviewsAnalyticsIdentity) {
      reviewsIdentity = analyticsIdentity
      reviewsIdentityResolved = true
      tryIdentify()
    }

    function tryIdentify() {
      if (hasIdentified || !userId.value || !reviewsIdentityResolved) return
      hasIdentified = true
      const company = reviewsIdentity?.company
      identify({
        user_id: userId.value.split('/').pop() ?? userId.value,
        ...(reviewsIdentity ? { account_type: ACCOUNT_TYPE_BY_ENUM[reviewsIdentity.accountType] } : {}),
        ...(company
          ? {
              company_id: company.id,
              company_name: company.name ?? undefined,
              company_slug: company.slug,
              plan_tier: company.planTier ?? undefined,
            }
          : {}),
      })
    }

    function setupCurrentUser(data: MeQuery) {
      if (data?.me) {
        rawMe.value = data.me
        if (!isProfileComplete(data.me.profileScopes)) {
          redirectToLogin(localeStore.locale || 'de')
          return
        }

        profileScopes.value = data.me.profileScopes
        hasAcceptedReviewsTerms.value = profileScopes.value.includes(ProfileScopeEnum.ReviewsTerms)

        roles.value = data.me.roles
        if (data.me.partnerCompany?.id) {
          setCurrentPartnerCompanyGlobalId(data.me.partnerCompany.id)
        } else {
          currentPartnerCompanyGlobalId.value = undefined
        }

        userFirstName.value = data.me.firstName || ''
        userLastName.value = data.me.lastName || ''
        userEmail.value = data.me.email || ''
        userProfileImagePath.value = data.me.profileImage || ''
        userId.value = data.me.id

        setupSentryUser(data.me)
        tryIdentify()
      }
      if (data?.booths?.nodes?.length) {
        boothsVisible.value = true
        setHasPremiumBooths(data.booths.nodes)
      }
    }
    function setHasPremiumBooths(boothList: MeQuery['booths']['nodes']) {
      const FIRST_VALID_DATE = new Date('11.29.2022.')
      const validBooths = boothList.filter((booth: MeQuery['booths']['nodes'][number]) => {
        return booth.premium === true && FIRST_VALID_DATE <= new Date(booth.createdAt as string)
      })
      if (validBooths.length) hasPremiumBooths.value = true
    }

    return {
      boothsVisible,
      currentPartnerCompanyGlobalId,
      currentPartnerCompanyId,
      hasCurrentPartnerCompany,
      currentRoles,
      hasAcceptedReviewsTerms,
      hasPremiumBooths,
      profileScopes,
      leadGenLimit,
      roles,
      setCurrentPartnerCompanyGlobalId,
      setupCurrentUser,
      identifyUser,
      me: rawMe,
      userEmail,
      userName,
      userProfileImage,
    }
  },
  {
    persist: {
      key: persistKey,
      pick: ['boothsVisible', 'currentPartnerCompanyGlobalId'],
      storage: localStorage,
    },
  },
)
